import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  makeStyles
} from "@material-ui/core";
import { get } from "../../../api/api";
import { Currency } from "../../../components/misc/Currency";
import { ReceiptItem } from "../../../models/User";
import { useI18n } from "../../../hooks/useI18n";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    maxWidth: "100%"
  },
  title: {
    marginBottom: theme.spacing(2)
  },
  headerInfo: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    padding: `${theme.spacing(1)}px 0`,
    borderBottom: "1px solid #eee"
  },
  itemInfo: {
    flex: 1
  },
  productName: {
    fontWeight: 500
  },
  productMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: 2
  },
  priceColumn: {
    textAlign: "right",
    paddingLeft: theme.spacing(2)
  },
  discounted: {
    textDecoration: "line-through",
    color: theme.palette.text.secondary,
    fontSize: "0.85rem"
  },
  total: {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: 500,
    padding: `${theme.spacing(2)}px 0`
  },
  totalAmount: {
    fontWeight: 700
  },
  button: {
    marginTop: theme.spacing(2)
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4)
  },
  messageText: {
    marginLeft: theme.spacing(2)
  }
}));

const fetchReceiptItems = async (
  userId: string,
  chargeId: string
): Promise<ReceiptItem[]> => {
  const response = await get(
    `/v1/users/${userId}/receipt-items?chargeId=${chargeId}`
  );
  return response.json();
};

const UserPaymentEvent: React.FC<{
  userId: string;
  chargeId: string;
}> = ({ userId, chargeId }) => {
  const { t } = useI18n();
  const classes = useStyles();

  const { data, isLoading, isError, refetch } = useQuery(
    ["receiptItems", chargeId],
    () => fetchReceiptItems(userId, chargeId),
    {
      staleTime: Infinity
    }
  );

  const calculateTotal = (items: ReceiptItem[] = []) => {
    return items.reduce((sum, item) => {
      const effectivePrice = item.discountedPrice || item.price;
      return sum + effectivePrice * item.quantity;
    }, 0);
  };

  if (isLoading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={24} />
        <Typography variant="body2" className={classes.messageText}>
          Loading payment details
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className={classes.root}>
        <Typography variant="body1">
          {t("common.messages.failedToLoadPaymentDetails")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => refetch()}
          className={classes.button}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box className={classes.root}>
        <Typography variant="body1">
          {t("common.messages.noReceiptItemsAvailable")}
        </Typography>
      </Box>
    );
  }

  const total = calculateTotal(data);

  return (
    <Box className={classes.root}>
      <Box className={classes.headerInfo}>
        <Typography variant="body2" color="textSecondary">
          Receipt #{data[0]?.receiptId || "---"}
        </Typography>
      </Box>

      <Divider className={classes.divider} />

      {data.map(item => {
        const effectivePrice = item.discountedPrice || item.price;
        const subtotal = effectivePrice * item.quantity;

        return (
          <Box key={item.id} className={classes.item}>
            <Box className={classes.itemInfo}>
              <Typography className={classes.productName}>
                {item.productName}
              </Typography>
              <Typography className={classes.productMeta}>
                {item.quantity} × <Currency value={effectivePrice} />
              </Typography>
            </Box>
            <Box className={classes.priceColumn}>
              {item.discountedPrice && (
                <Typography className={classes.discounted}>
                  <Currency value={item.price} />
                </Typography>
              )}
              <Typography>
                <Currency value={subtotal} />
              </Typography>
            </Box>
          </Box>
        );
      })}

      <Divider className={classes.divider} />

      <Box className={classes.total}>
        <Typography>{t("common.messages.total")}</Typography>
        <Typography className={classes.totalAmount}>
          <Currency value={total} />
        </Typography>
      </Box>
    </Box>
  );
};

export default UserPaymentEvent;
